import { OutputSettingsService } from '@service/output-settings.service';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { SlotSelectorComponent } from '@components/slot-selector/slot-selector.component';
import { OutputSettingsType, OutputSettingTab } from '@models/output-settings';
import { ChannelService } from '@service/channel/channel.service';
import { SensorCableTagService } from '@service/sensor-cable-tag/sensor-cable-tag.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType } from 'icreate/common/models/channel-type';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom } from 'rxjs';
import { AnalogOutputSettingsTableComponent } from './analog-output-settings-table/analog-output-settings-table.component';
import { OutputSetting } from 'icreate/common/models/output-settings';
import { MessageDialogService } from '@service/message-dialog.service';
import { DigitalOutputSettingComponent } from '@components/digital-output-setting/digital-output-setting.component';
import { VesselService } from '@service/vessel.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-output-settings',
    templateUrl: './output-settings.component.html',
    styleUrl: './output-settings.component.css',
    standalone: false,
})
export class OutputSettingsComponent implements OnInit {
    readonly TABS = Object.values(OutputSettingTab);
    readonly DO_TERMINALS = OutputSettingTab.DO_TERMINALS;
    readonly AO_TERMINALS = OutputSettingTab.AO_TERMINALS;

    @Input() vessel: Vessel;
    @ViewChild('slotSelector') slotSelector: SlotSelectorComponent;
    @ViewChild(AnalogOutputSettingsTableComponent)
    analogSettingsComponent: AnalogOutputSettingsTableComponent;
    @ViewChild(DigitalOutputSettingComponent)
    digitalSettingsComponent: DigitalOutputSettingComponent;

    vesselSlots: SensorTagSlot[];
    channels: Channel[];
    selectedSettingTab: OutputSettingTab;
    selectedSetting: OutputSetting;
    selectedSlot: SensorTagSlot;
    hasSelectedTerminal: boolean = false;
    showDigitalSettings: boolean = false;
    isLoading: boolean = false;
    readonly canEditVessel = toSignal(this.vesselService.accessUpdated$, { initialValue: false });

    constructor(
        private sensorCableTagService: SensorCableTagService,
        private outputSettingsService: OutputSettingsService,
        private channelService: ChannelService,
        private messageDialogService: MessageDialogService,
        private cdr: ChangeDetectorRef,
        private vesselService: VesselService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        try {
            const slots: any[] = await this.sensorCableTagService.getVesselSlots(this.vessel.id);
            this.channels = (await lastValueFrom(this.channelService.getChannels(this.vessel.id)))
                .filter(channel => channel.fcuData.channelType !== ChannelType.BLANK)
                .sort((a, b) =>
                    a.fcuData.fcuChannelId.localeCompare(b.fcuData.fcuChannelId, ['en', 'jp'], {
                        numeric: true,
                        sensitivity: 'variant',
                    })
                );
            if (slots?.length > 0) {
                this.vesselSlots = slots.map(slot => {
                    const vesselSlot = {
                        no: slot.no,
                        slotId: slot.id,
                        ioType: slot.ioType,
                        fcuNum: slot.FcuUnit?.index,
                        fcuId: slot.FcuUnit?.id,
                        fuNum: slot.FuUnit?.index,
                        fuId: slot.FuUnit?.id,
                        terminal: slot.terminal,
                        vesselId: this.vessel.id,
                    };
                    return vesselSlot;
                });
            }
        } catch (err) {
            console.error(err);
        }

        this.isLoading = false;
    }

    getSettingsType() {
        if (this.selectedSettingTab == OutputSettingTab.AO_TERMINALS) return OutputSettingsType.AO;
        else if (this.selectedSettingTab == OutputSettingTab.DO_TERMINALS) return OutputSettingsType.DO;
    }

    selectTab(tab: OutputSettingTab) {
        if (tab) this.selectedSettingTab = tab;
        else {
            this.selectedSlot = undefined;
            if (this.slotSelector) this.slotSelector.unselectSlot();
            this.selectedSettingTab = null;
        }
    }

    handleSelectedSlot(slot: SensorTagSlot) {
        if (!this.canEditVessel()) return;
        this.selectedSlot = slot;
    }

    handleCancel() {
        this.selectedSlot = undefined;
        if (this.slotSelector) this.slotSelector.unselectSlot();
    }

    handleCancelDO() {
        this.showDigitalSettings = false;
    }

    async handleDelete() {
        if (!this.selectedSetting || !this.canEditVessel()) return;
        try {
            this.isLoading = true;
            await this.outputSettingsService.deleteDOTerminals(
                this.selectedSlot.slotId,
                [this.selectedSetting.terminalNum],
                this.vessel.id
            );
            this.isLoading = false;
            this.cdr.detectChanges();
            this.selectedSetting = {
                vesselId: this.selectedSlot.vesselId,
                slotId: this.selectedSlot.slotId,
                terminalNum: this.selectedSetting.terminalNum,
                channels: [],
            };
            this.showDigitalSettings = false;
            this.showDeletedSingleMessage(this.selectedSetting.terminalNum);
        } catch (err) {
            console.error(err);
            this.isLoading = false;
        }
    }

    async shouldLeaveConfirmation() {
        if (this.selectedSettingTab == OutputSettingTab.AO_TERMINALS && this.analogSettingsComponent) {
            const shouldLeave = await this.analogSettingsComponent.shouldLeaveConfirmation();
            return shouldLeave;
        } else if (this.selectedSettingTab == OutputSettingTab.DO_TERMINALS && this.digitalSettingsComponent) {
            const shouldLeave = await this.digitalSettingsComponent.shouldLeaveConfirmation();
            return shouldLeave;
        }

        return true;
    }

    handleSelectedDigitalSetting(setting: OutputSetting) {
        if (setting) {
            if (setting.id) this.showDigitalSettings = true;
            this.selectedSetting = setting;
        } else {
            this.selectedSetting = null;
            this.showDigitalSettings = false;
        }
    }

    getSlotTileArray() {
        if (this.selectedSettingTab == OutputSettingTab.AO_TERMINALS || !this.selectedSetting) return;
        return this.outputSettingsService.getSlotTileArray(this.selectedSlot, this.selectedSetting);
    }

    showDeletedSingleMessage(terminalNum) {
        this.messageDialogService.openMessage('output-settings.DO.notification.deleted', {
            num: this.outputSettingsService.paddNumber(terminalNum),
        });
    }
}
